import React, { useMemo, useState } from 'react';
import { CheckCircle2, KeyRound, ShieldCheck, XCircle } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiClient, type SecurityDeviceAuthorizationResult } from '../lib/api-client';

function normalizeUserCode(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
}

function displayUserCode(value: string): string {
  const normalized = normalizeUserCode(value);
  return normalized.length > 4
    ? `${normalized.slice(0, 4)}-${normalized.slice(4)}`
    : normalized;
}

export const DeviceAuthorizationPage: React.FC = () => {
  const initialCode = useMemo(
    () => normalizeUserCode(new URLSearchParams(window.location.search).get('user_code') || ''),
    [],
  );
  const [userCode, setUserCode] = useState(initialCode);
  const [result, setResult] = useState<SecurityDeviceAuthorizationResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);

  const submit = async (decision: 'approve' | 'deny') => {
    const normalized = normalizeUserCode(userCode);
    if (normalized.length < 8) {
      setError('Enter the complete code shown by the Linux agent.');
      return;
    }
    setBusy(decision);
    setError('');
    try {
      const response = decision === 'approve'
        ? await apiClient.approveSecurityDevice(normalized)
        : await apiClient.denySecurityDevice(normalized);
      setResult(response);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The device request could not be updated.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-xl">
        <Card className="overflow-hidden border-white/80 bg-white/95 shadow-xl shadow-indigo-950/10 dark:border-white/10 dark:bg-card/95">
          <CardHeader className="border-b bg-slate-50/80 dark:bg-slate-950/40">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
              <KeyRound className="h-6 w-6" />
            </div>
            <CardTitle>Connect a Linux security agent</CardTitle>
            <CardDescription>
              Authorize only a device you are actively installing. The agent will receive a tenant-bound credential; it will not receive your dashboard session.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 pt-6">
            {result ? (
              <div className={`rounded-xl border p-5 ${
                result.status === 'approved'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100'
                  : 'border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100'
              }`}>
                <div className="flex items-center gap-3">
                  {result.status === 'approved'
                    ? <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                    : <XCircle className="h-6 w-6 text-slate-500" />}
                  <div>
                    <p className="font-semibold">
                      {result.status === 'approved' ? 'Device approved' : 'Device request denied'}
                    </p>
                    <p className="mt-1 text-sm opacity-80">
                      {result.status === 'approved'
                        ? 'Return to the terminal. The agent will finish enrollment without displaying its credential.'
                        : 'The waiting agent will be told that authorization was denied.'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Input
                  label="Device code"
                  value={displayUserCode(userCode)}
                  onChange={(event) => setUserCode(normalizeUserCode(event.target.value))}
                  autoComplete="one-time-code"
                  inputMode="text"
                  maxLength={13}
                  placeholder="ABCD-EFGH"
                  helperText="Compare this code with the one printed by loglimit-linux-agent login."
                  error={error || undefined}
                />
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                  <div className="flex gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
                    <p>Approval permits this host to send security events to your organization. It does not permit dashboard or MCP administration.</p>
                  </div>
                </div>
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <Button
                    variant="outline"
                    disabled={Boolean(busy)}
                    onClick={() => void submit('deny')}
                  >
                    {busy === 'deny' ? 'Denying…' : 'Deny'}
                  </Button>
                  <Button
                    disabled={Boolean(busy)}
                    onClick={() => void submit('approve')}
                  >
                    {busy === 'approve' ? 'Approving…' : 'Approve device'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
};
